// Contexto de autenticación
// RecomiendaT - Sistema de recomendaciones

import React, { createContext, useContext, useState, useEffect } from 'react';
import databaseService from '../utils/database';
import config from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, navigate }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar si hay un token válido al cargar la aplicación
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem(config.AUTH_TOKEN_KEY);
        console.log('🔍 Verificando autenticación...', { 
          token: token ? token.substring(0, 20) + '...' : 'No token'
        });
        
        if (token) {
          console.log('📡 Llamando a getCurrentUser...');
          const response = await databaseService.getCurrentUser();
          console.log('✅ Respuesta del backend:', response);
          
          if (response && response.user) {
            console.log('✅ Usuario obtenido:', response.user);
            setUser(response.user);
            setIsAuthenticated(true);
          } else {
            console.log('❌ No se encontró usuario en la respuesta');
            localStorage.removeItem(config.AUTH_TOKEN_KEY);
          }
        } else {
          console.log('❌ No hay token en localStorage');
        }
      } catch (error) {
        console.error('❌ Error verificando autenticación:', error);
        // Si hay error, limpiar el token inválido
        localStorage.removeItem(config.AUTH_TOKEN_KEY);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Función de login
  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await databaseService.login(email, password);
      
      if (response.success && response.user) {
        // Usar el token real del backend o crear uno temporal
        const token = response.token || 'simple_token_' + Date.now();
        console.log('💾 Guardando token en login:', { 
          token: token.substring(0, 20) + '...', 
          tokenLength: token.length,
          key: config.AUTH_TOKEN_KEY
        });
        localStorage.setItem(config.AUTH_TOKEN_KEY, token);
        console.log('✅ Token guardado en localStorage');
        setUser(response.user);
        setIsAuthenticated(true);
        
        // Redirigir al dashboard después del login exitoso
        // Usar setTimeout para asegurar que el estado se actualice antes de navegar
        console.log('Login exitoso, intentando redirigir...', { navigate: !!navigate });
        setTimeout(() => {
          if (navigate) {
            console.log('Navegando a /dashboard');
            navigate('/dashboard');
          } else {
            console.log('navigate no está disponible');
          }
        }, 50);
        
        return { success: true, user: response.user };
      } else {
        return { success: false, message: response.message || 'Error en la respuesta del servidor' };
      }
    } catch (error) {
      console.error('Error en login:', error);
      return { 
        success: false, 
        message: error.message || 'Error al iniciar sesión' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Función de registro
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await databaseService.register(userData);
      
      if (response.success && response.user) {
        // Usar el token real del backend o crear uno temporal
        const token = response.token || 'simple_token_' + Date.now();
        console.log('💾 Guardando token en registro:', { 
          token: token.substring(0, 20) + '...', 
          tokenLength: token.length,
          key: config.AUTH_TOKEN_KEY
        });
        localStorage.setItem(config.AUTH_TOKEN_KEY, token);
        console.log('✅ Token guardado en localStorage');
        setUser(response.user);
        setIsAuthenticated(true);
        
        // Redirigir al dashboard después del registro exitoso
        // Usar setTimeout para asegurar que el estado se actualice antes de navegar
        console.log('Registro exitoso, intentando redirigir...', { navigate: !!navigate });
        setTimeout(() => {
          if (navigate) {
            console.log('Navegando a /dashboard');
            navigate('/dashboard');
          } else {
            console.log('navigate no está disponible');
          }
        }, 50);
        
        return { success: true, user: response.user };
      } else {
        return { success: false, message: response.message || 'Error en la respuesta del servidor' };
      }
    } catch (error) {
      console.error('Error en registro:', error);
      return { 
        success: false, 
        message: error.message || 'Error al registrarse' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Función de logout
  const logout = async () => {
    try {
      await databaseService.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      localStorage.removeItem(config.AUTH_TOKEN_KEY);
      setUser(null);
      setIsAuthenticated(false);
      // Redirigir al home después del logout
      if (navigate) {
        navigate('/');
      }
    }
  };

  // Actualizar perfil propio (nombre/telefono)
  const updateProfile = async (profileData) => {
    try {
      const resp = await databaseService.updateProfile(profileData);
      if (resp?.success && resp?.user) {
        setUser((prev)=> ({ ...prev, ...resp.user }));
        return { success: true, user: resp.user };
      }
      return { success: false, message: resp?.message || 'Error al actualizar perfil' };
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      return { success: false, message: error.message || 'Error' };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
